import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  confirmIep,
  fetchConfirmedIep,
  fetchIepDraft,
  fetchIepVersions,
  generateIep,
  saveIepDraft,
  type ConfirmedIep,
  type IepContent,
  type IepDraft
} from "@/services/repositories/assessment-repository";
import { ApiError } from "@/services/api/client";
import { useVersionedAutosave } from "@/hooks/useVersionedAutosave";

export function useIepDraft({
  assessmentId,
  defaultContent,
  defaultsReady
}: {
  assessmentId: string | undefined;
  defaultContent: IepContent;
  defaultsReady: boolean;
}) {
  const queryClient = useQueryClient();
  const [content, setContent] = useState<IepContent>();
  const contentRef = useRef<IepContent>();
  const sourceAttemptRef = useRef<string>();
  const formalVersionRef = useRef(0);
  const draftVersionRef = useRef(0);
  const initializedAssessmentRef = useRef<string>();

  const draftQuery = useQuery({
    queryKey: ["iepDraft", assessmentId],
    enabled: Boolean(assessmentId),
    queryFn: () => fetchIepDraft(assessmentId!),
    staleTime: 30 * 1000
  });
  const formalQuery = useQuery({
    queryKey: ["confirmedIep", assessmentId],
    enabled: Boolean(assessmentId),
    queryFn: async () => {
      try {
        return await fetchConfirmedIep(assessmentId!);
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) return null;
        throw error;
      }
    },
    staleTime: 30 * 1000
  });
  const versionsQuery = useQuery({
    queryKey: ["iepVersions", assessmentId],
    enabled: Boolean(assessmentId),
    queryFn: () => fetchIepVersions(assessmentId!),
    staleTime: 30 * 1000
  });

  const {
    reset,
    queue,
    flush: flushAutosave,
    retry,
    status: saveStatus,
    error: saveError,
    version: draftVersion
  } = useVersionedAutosave<IepContent, IepDraft>({
    initialVersion: 0,
    enabled: Boolean(assessmentId),
    save: (payload, expectedVersion) =>
      saveIepDraft(assessmentId!, payload, expectedVersion, sourceAttemptRef.current),
    onSaved: (saved) => {
      draftVersionRef.current = saved.version;
      sourceAttemptRef.current = saved.sourceGenerationAttemptId;
      queryClient.setQueryData(["iepDraft", saved.assessmentId], { draft: saved });
    }
  });

  useEffect(() => {
    if (!assessmentId) {
      initializedAssessmentRef.current = undefined;
      contentRef.current = undefined;
      sourceAttemptRef.current = undefined;
      formalVersionRef.current = 0;
      draftVersionRef.current = 0;
      setContent(undefined);
      reset(0);
      return;
    }
    if (
      initializedAssessmentRef.current === assessmentId ||
      draftQuery.isLoading ||
      formalQuery.isLoading ||
      draftQuery.isError ||
      formalQuery.isError ||
      (!draftQuery.data?.draft && !formalQuery.data && !defaultsReady)
    ) {
      return;
    }
    const draft = draftQuery.data?.draft;
    const formal = formalQuery.data;
    const initial = draft?.content ?? formal?.content ?? defaultContent;
    initializedAssessmentRef.current = assessmentId;
    contentRef.current = initial;
    sourceAttemptRef.current =
      draft?.sourceGenerationAttemptId ?? formal?.sourceGenerationAttemptId;
    formalVersionRef.current = formal?.version ?? 0;
    draftVersionRef.current = draft?.version ?? 0;
    setContent(initial);
    reset(draft?.version ?? 0);
  }, [
    assessmentId,
    defaultContent,
    defaultsReady,
    draftQuery.data,
    draftQuery.isError,
    draftQuery.isLoading,
    formalQuery.data,
    formalQuery.isError,
    formalQuery.isLoading,
    reset
  ]);

  const updateContent = useCallback(
    (update: IepContent | ((previous: IepContent) => IepContent)) => {
      const previous = contentRef.current;
      if (!previous) return;
      const next = typeof update === "function" ? update(previous) : update;
      contentRef.current = next;
      setContent(next);
      queue(next);
    },
    [queue],
  );

  const flush = useCallback(
    async ({ forceCreate = false }: { forceCreate?: boolean } = {}) => {
      const current = contentRef.current;
      if (!current) return false;
      const saved = await flushAutosave({ payload: current, force: forceCreate });
      draftVersionRef.current = saved ? Math.max(draftVersionRef.current, draftVersion) : draftVersionRef.current;
      return saved;
    },
    [draftVersion, flushAutosave],
  );

  const applyGeneratedContent = useCallback(
    (generated: { content: IepContent; draftVersion: number; attemptId: string }) => {
      contentRef.current = generated.content;
      sourceAttemptRef.current = generated.attemptId;
      draftVersionRef.current = generated.draftVersion;
      setContent(generated.content);
      reset(generated.draftVersion);
      void queryClient.invalidateQueries({ queryKey: ["iepDraft", assessmentId] });
    },
    [assessmentId, queryClient, reset],
  );

  const generate = useCallback(
    async (requestId: string) => {
      const flushed = await flush({ forceCreate: draftVersionRef.current === 0 });
      if (!flushed) throw new Error("当前 IEP 草稿尚未保存，已取消 AI 生成");
      const generated = await generateIep(
        assessmentId!,
        requestId,
        draftVersionRef.current,
      );
      if (!generated.applied) throw new Error("AI 结果未应用到服务器草稿");
      applyGeneratedContent(generated);
      return generated;
    },
    [applyGeneratedContent, assessmentId, flush],
  );

  const confirm = useCallback(async (): Promise<ConfirmedIep> => {
    const current = contentRef.current;
    if (!assessmentId || !current) throw new Error("IEP 内容尚未准备完成");
    const flushed = await flush({ forceCreate: draftVersionRef.current === 0 });
    if (!flushed) throw new Error("当前 IEP 草稿尚未保存，已取消正式确认");
    const saved = await confirmIep(
      assessmentId,
      current,
      formalVersionRef.current,
      draftVersionRef.current,
      sourceAttemptRef.current,
    );
    formalVersionRef.current = saved.version;
    draftVersionRef.current = 0;
    sourceAttemptRef.current = saved.sourceGenerationAttemptId;
    reset(0);
    queryClient.setQueryData(["confirmedIep", assessmentId], saved);
    queryClient.setQueryData(["iepDraft", assessmentId], { draft: null });
    await queryClient.invalidateQueries({ queryKey: ["iepVersions", assessmentId] });
    return saved;
  }, [assessmentId, flush, queryClient, reset]);

  const reload = useCallback(async () => {
    if (!assessmentId) return false;
    const [draftResult, formalResult] = await Promise.all([
      draftQuery.refetch(),
      formalQuery.refetch()
    ]);
    if (draftResult.error || formalResult.error) return false;
    const draft = draftResult.data?.draft;
    const formal = formalResult.data;
    const next = draft?.content ?? formal?.content ?? defaultContent;
    initializedAssessmentRef.current = assessmentId;
    contentRef.current = next;
    sourceAttemptRef.current =
      draft?.sourceGenerationAttemptId ?? formal?.sourceGenerationAttemptId;
    formalVersionRef.current = formal?.version ?? 0;
    draftVersionRef.current = draft?.version ?? 0;
    setContent(next);
    reset(draft?.version ?? 0);
    return true;
  }, [assessmentId, defaultContent, draftQuery, formalQuery, reset]);

  return {
    content,
    updateContent,
    flush,
    retry,
    reload,
    generate,
    confirm,
    saveStatus,
    saveError,
    draftVersion,
    formalVersion: formalQuery.data?.version ?? formalVersionRef.current,
    versions: versionsQuery.data ?? [],
    hasGeneratedDraft: Boolean(
      sourceAttemptRef.current && draftVersionRef.current > 0,
    ),
    isLoading:
      draftQuery.isLoading ||
      formalQuery.isLoading ||
      (Boolean(assessmentId) && !content),
    loadError: draftQuery.error ?? formalQuery.error,
    versionsLoading: versionsQuery.isLoading
  };
}
